<script setup lang="ts">
import { VueYandexMaps } from 'vue-yandex-maps'
import type { Position } from 'vue-yandex-maps/dist/types/marker'

import { STATE_OF_MAP } from '@/constants/statesOfAddressSystem'

import type { DeliveryDataAddress } from '@/store/delivery/delivery.types'
import { BalloonType } from '@/store/delivery/delivery.types'
import { useMapSettingsStore } from '@/store/map-settings/map-settings'

import { TypeMaps, TypesProvider } from '@/types/mapServices'

import AddressSystemMapYandex from '@/components/AddressSystem/map/AddressSystemMapYandex.vue'
import DeliveryMapError from '@/components/Delivery/DeliveryMapError.vue'
import GoogleMapComponent from '@/components/Services/GoogleMap/GoogleMapComponent.vue'
import YandexMapComponent from '@/components/Services/YandexMap/YandexMapComponent.vue'

import type {
  AreaProperties,
  DragCoordinates,
  LatLng,
  PointProperties,
  PolygonProperties,
} from '@/api/modules/delivery/types'

type Props = {
  positionControl: Position
  areas: AreaProperties[]
  points: PointProperties[]
  deliveryAddress: DeliveryDataAddress
  selectedRestaurant: PointProperties | null
  isLoadingAreas: boolean
  balloonType?: BalloonType
  isDeliveryCourier?: boolean
  componentMap: TypeMaps
}

type Emits = {
  (fn: 'onUpdateCenterMap'): void
  (
    fn: 'onClickToMap',
    newCoords: LatLng,
    deliveryDetails: PolygonProperties | null,
  ): void
  (fn: 'onClickMarker'): void
  (fn: 'onUpdateDistance', coords: LatLng): void
  (fn: 'onClickRestaurant', restaurant: PointProperties): void
  (
    fn: 'onUpdateAreas',
    bounds: DragCoordinates,
    isUpdateDistance: boolean,
  ): void
}

const props = defineProps<Props>()

const emit = defineEmits<Emits>()

const { $metrics } = useNuxtApp()

enum TypeComponentMaps {
  YANDEX_DELIVERY = 'DELIVERY',
  YANDEX_ADDRESS_SYSTEM = 'ADDRESS_SYSTEM',
  GOOGLE_DELIVERY = 'GOOGLE_DELIVERY',
}

const COMPONENT_MAPS: Record<TypeComponentMaps, Component> = {
  [TypeComponentMaps.YANDEX_DELIVERY]: YandexMapComponent,
  [TypeComponentMaps.YANDEX_ADDRESS_SYSTEM]: AddressSystemMapYandex,
  [TypeComponentMaps.GOOGLE_DELIVERY]: GoogleMapComponent,
}

const { $sentryCaptureException } = useNuxtApp()

const { isDesktop } = useResponsive()

const mapSettingsStore = useMapSettingsStore()

const currentMap = ref<Component | null>(null)

const serviceSettings = mapSettingsStore.mapSettings.service_settings

// Инициализация модуля Яндекс карты в процессе setup
const { createYmapsOptions } = await import('vue-yandex-maps')

const isStateLoadMap = computed(() => mapSettingsStore.stateLoadMap)

// Состояние что карта загружена
const isMapLoaded = computed(() => isStateLoadMap.value === STATE_OF_MAP.loaded)

const initModuleYandexMap = () => {
  const { provider, api_key } = serviceSettings

  // Для гугла не требуется вручную прокидывать инстанс карты
  if (provider === TypesProvider.GOOGLE) {
    currentMap.value = COMPONENT_MAPS[TypeComponentMaps.GOOGLE_DELIVERY]
    return
  }

  const isComponent =
    props.componentMap === TypeMaps.DELIVERY
      ? COMPONENT_MAPS[TypeComponentMaps.YANDEX_DELIVERY]
      : COMPONENT_MAPS[TypeComponentMaps.YANDEX_ADDRESS_SYSTEM]

  currentMap.value = isComponent

  if (provider === TypesProvider.YANDEX) createYmapsOptions({ apikey: api_key })
}

watch(VueYandexMaps.loadStatus, (newStatus: VueYandexMaps.LoadStatus) => {
  mapSettingsStore.setStateLoadMap(newStatus)

  if (newStatus !== STATE_OF_MAP.loaded) return

  $metrics?.eventSendGoal({ ym: 'map_loaded_for_address' })
})

// Т.к модуль не предоставляет тип, property, оставляем any
watch(VueYandexMaps.loadError, (newLoadError: any) => {
  if (!newLoadError) return

  mapSettingsStore.setStateLoadMap(STATE_OF_MAP.error)

  $sentryCaptureException(
    'Не удалось загрузить яндекс карту',
    VueYandexMaps.loadError,
  )
})

const onClickToMap = (
  newCoords: LatLng,
  deliveryDetails: PolygonProperties | null,
) => emit('onClickToMap', newCoords, deliveryDetails)

const clickRestaurant = (restaurant: PointProperties) => {
  emit('onClickRestaurant', restaurant)
}

const updateCenterMap = () => emit('onUpdateCenterMap')

const updateDistance = (coords: LatLng) => emit('onUpdateDistance', coords)

const updateAreas = (bounds: DragCoordinates, isUpdateDistance: boolean) =>
  emit('onUpdateAreas', bounds, isUpdateDistance)

const onClickMarker = () => emit('onClickMarker')

onMounted(() => {
  initModuleYandexMap()

  mapSettingsStore.setStateLoadMap(STATE_OF_MAP.loaded)
})
</script>

<template>
  <div class="delivery-map">
    <the-loader
      v-if="isStateLoadMap === STATE_OF_MAP.loading"
      color="var(--ui-secondary)"
    />

    <component
      :is="currentMap"
      v-if="isMapLoaded"
      class="delivery-map__yandex"
      :settings="serviceSettings"
      width="100%"
      :areas="areas"
      :points="points"
      :clicked-restaurant="selectedRestaurant"
      :selected-address="deliveryAddress"
      :position-control="positionControl"
      :balloon-type="balloonType"
      :is-delivery-courier="isDeliveryCourier"
      @on-click-to-map="onClickToMap"
      @on-click-restaurant="clickRestaurant"
      @on-click-marker="onClickMarker"
      @on-update-center-map="updateCenterMap"
      @on-update-distance="updateDistance"
      @on-update-areas="updateAreas"
    />

    <delivery-map-error
      v-if="isStateLoadMap === STATE_OF_MAP.error"
      class="delivery-map__error"
    />

    <transition v-if="isDesktop" name="ui-fade">
      <app-loader-rolls
        v-show="isLoadingAreas"
        class="loader-areas"
        icon-size="21"
        absolute
      />
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.loader-areas {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
}

.delivery-map {
  position: relative;
  width: 100%;

  &__yandex {
    height: 100% !important;
  }

  &__google {
    width: 100%;
    height: 100%;
  }

  &__error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
}
</style>
